import { useEffect, useMemo } from "react";
import { Box, Button, Divider, Grid, Stack } from "@mui/material";

import { ControlledAutocomplete, DataLayout, FieldControl, FileUpload } from "../../../shared/components";
import { useRaiseQuery } from "../hooks/useRaiseQuery";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { filterCommercialCategory } from "../../../shared/utils/filterCommercialCategory";
import { useGetJeopardyStakeholders } from "../../../shared/hooks";

export const RaiseQuery: React.FC<{ id: null | string; projectId: string | undefined; editStatus: boolean; onSaved: () => void; onClose: () => void }> = ({ id, projectId, editStatus, onSaved, onClose }) => {

    const { getFilterOptions } = useDisplayField([]);
    const { isCommercialPerson, isAdminPerson } = useCapabilities();
    const { query, isPending, fileUploadRef, onFileUploadCountChange, phaseData, handleUpdate, onSave, queryErrors } = useRaiseQuery({ id, projectId, editStatus });

    const params = () => {
        if (projectId != undefined) {
            return { project_id: projectId };
        } else if (query.type === "Project" && query.project?.id) {
            return { project_id: query.project.id };
        } else if (query.type === "Programme" && query.programme?.id) {
            return { programme_id: query.programme.id };
        } else if (query.type === "Portfolio" && query.portfolio?.id) {
            return { portfolio_id: query.portfolio.id };
        }
        return {};
    };

    const { data: stakeholders, isLoading: isLoadingUsers, refetch } = useGetJeopardyStakeholders({ params: params() });

    const userOptions = useMemo(() => {
        return stakeholders?.data?.flatMap((role: any) =>
            role?.users?.map((user: any) => ({
                value: user.user_id,
                label: user.user_name,
            }))
        ) || [];
    }, [stakeholders]);

    useEffect(() => {
        const currentProjectId = projectId || (query.type === "Project" ? query.project?.id : query.type === "Programme" ? query.programme?.id : query.type === "Portfolio" ? query.portfolio?.id : undefined);
        if (currentProjectId) {
            refetch();
        }
    }, [projectId, query.type, query.project?.id, query.programme?.id, query.portfolio?.id, refetch]);

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} height={'100%'} overflow={'hidden'}>
            <Box flex={1} sx={{ overflowY: 'auto' }} p={2}>
                <Box display={'flex'} flexDirection={'column'} gap={2}>

                    <FieldControl
                        keyName={'type'}
                        label={"Query Type"}
                        variant={"select"}
                        options={[
                            { label: 'Project', value: 'Project' },
                            { label: 'Portfolio', value: 'Portfolio' },
                            { label: 'Programme', value: 'Programme' },
                        ]}
                        value={query.type}
                        onChange={handleUpdate}
                        required
                        error={!!queryErrors.type}
                        helperText={queryErrors.type}
                    />

                    {
                        query.type === 'Project' &&
                        <ControlledAutocomplete
                            type="PROJECT"
                            value={query.project}
                            onSelect={(val) => handleUpdate('project', val)}
                            label="Search project"
                            placeholder="Search project"
                            required
                            error={!!queryErrors.project}
                            helperText={queryErrors.project}
                        />
                    }

                    {
                        query.type === 'Project' &&
                        <FieldControl
                            keyName={'phase'}
                            label={"Select Project Phase"}
                            variant={"select"}
                            value={query.phase}
                            options={
                                phaseData.data?.data?.map((value) => {
                                    return {
                                        value: value.id, label: value.name
                                    }
                                })
                            }
                            onChange={handleUpdate}
                            loading={phaseData.isPending}
                            required
                            error={!!queryErrors.phase}
                            helperText={queryErrors.phase}
                        />
                    }

                    {
                        query.type === 'Portfolio' &&
                        <ControlledAutocomplete
                            type="PORTFOLIO"
                            value={query.portfolio}
                            onSelect={(val) => handleUpdate('portfolio', val)}
                            label="Search portfolio"
                            placeholder="Search portfolio"
                            required
                            error={!!queryErrors.portfolio}
                            helperText={queryErrors.portfolio}
                        />
                    }

                    {
                        query.type === 'Programme' &&
                        <ControlledAutocomplete
                            type="PROGRAMME"
                            value={query.programme}
                            onSelect={(val) => handleUpdate('programme', val)}
                            label="Search programme"
                            placeholder="Search programme"
                            required
                            error={!!queryErrors.programme}
                            helperText={queryErrors.programme}
                        />
                    }

                    <Divider />

                    <FieldControl
                        keyName={'title'}
                        label={"Query Title"}
                        variant={"input"}
                        value={query.title}
                        onChange={handleUpdate}
                        required
                        error={!!queryErrors.title}
                        helperText={queryErrors.title}
                    />

                    <FieldControl
                        keyName={'description'}
                        label={"Description"}
                        variant={"input"}
                        value={query.description}
                        onChange={handleUpdate}
                        required
                        error={!!queryErrors.description}
                        helperText={queryErrors.description}
                    />

                    <Grid container spacing={2}>
                        <Grid size={6}>
                            <FieldControl
                                keyName={'category'}
                                label={"Query Type"}
                                variant={"select"}
                                options={filterCommercialCategory({ domain: "QUERY_CATEGORY", filterValue: "Commercial", isInclude: isCommercialPerson, isAdminPerson }).map((value) => {
                                    return {
                                        label: value.display_text,
                                        value: value.key
                                    }
                                })}
                                value={query.category}
                                onChange={handleUpdate}
                                required
                                error={!!queryErrors.category}
                                helperText={queryErrors.category}
                            />
                        </Grid>
                        <Grid size={6}>
                            <FieldControl
                                keyName={'priority'}
                                label={"Priority"}
                                variant={"select"}
                                options={getFilterOptions('QUERY_PRIORITY').map((value) => {
                                    return {
                                        label: value.display_text,
                                        value: value.key
                                    }
                                })}
                                value={query.priority}
                                onChange={handleUpdate}
                                required
                                error={!!queryErrors.priority}
                                helperText={queryErrors.priority}
                            />
                        </Grid>
                    </Grid>

                    <FieldControl
                        keyName={'assigned'}
                        label={"Assigned to"}
                        variant={"select"}
                        options={userOptions}
                        loading={isLoadingUsers}
                        value={query.assigned}
                        onChange={handleUpdate}
                        required
                        error={!!queryErrors.assigned}
                        helperText={queryErrors.assigned}
                    />

                    <DataLayout title="Attachments" bgVariant="filled">
                        <Box p={2}>
                            <FileUpload
                                type={"queries"}
                                ref={fileUploadRef}
                                typeId={id}
                                onFilesChanged={onFileUploadCountChange}
                            />
                        </Box>
                    </DataLayout>

                </Box>
            </Box>
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0} bgcolor={'action.hover'} p={2}>
                <Button onClick={onClose} variant="outlined" loading={isPending} disabled={isPending}>Cancel</Button>
                <Button onClick={() => onSave(onSaved)} variant="contained" loading={isPending} disabled={isPending}>Save</Button>
            </Stack>
        </Box>
    )

}